import { css } from '@emotion/react';
import { useStore } from '@tanstack/react-form';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { formatFieldLabel } from '../../../../../../helpers';
import type { DynamicFormApi } from '../../hooks/use-app-form';
import { CARD_CAPTURE_RULES, getMediaFieldValidator, MediaValidationRules } from '../../media-upload';
import { useNonReactiveDynamicFormStore } from '../../store';
import type { DynamicFormField } from '../../types';
import { CardCaptureSideInput } from './card-capture-side-input';

const MAX_SIZE_MB = (CARD_CAPTURE_RULES.maxSizeBytes ?? 0) / 1024 / 1024;

export interface CardCaptureFieldProps {
  form: DynamicFormApi;
  fieldInfo: DynamicFormField;
}

export const CardCaptureField = ({ form, fieldInfo }: CardCaptureFieldProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const store = useNonReactiveDynamicFormStore();
  const required = !!fieldInfo.required;

  const rules: MediaValidationRules = { ...CARD_CAPTURE_RULES, maxFiles: 1, required };

  const frontKey = `${fieldInfo.key}.front`;
  const backKey = `${fieldInfo.key}.back`;

  // validators read attachment state from the store rather than the field value
  const frontValidator = getMediaFieldValidator({ fieldKey: frontKey, rules, t, getState: store.getState });
  const backValidator = getMediaFieldValidator({ fieldKey: backKey, rules, t, getState: store.getState });

  const fieldErrors = useStore(form.store, (state) => {
    const frontMeta = state.fieldMeta[frontKey];
    const backMeta = state.fieldMeta[backKey];
    const isTouched = frontMeta?.isTouched || backMeta?.isTouched;
    if (!isTouched) return [];
    const frontErrors = (frontMeta?.errors ?? []) as string[];
    const backErrors = (backMeta?.errors ?? []) as string[];
    return [...new Set([...frontErrors, ...backErrors])];
  });

  const frontHasError = useStore(form.store, (state) => {
    const meta = state.fieldMeta[frontKey];
    return !!meta?.isTouched && (meta?.errors?.length ?? 0) > 0;
  });
  const backHasError = useStore(form.store, (state) => {
    const meta = state.fieldMeta[backKey];
    return !!meta?.isTouched && (meta?.errors?.length ?? 0) > 0;
  });

  return (
    <fieldset css={fieldsetStyle} role='group' aria-required={required} aria-labelledby={`${fieldInfo.key}-label`}>
      <Text id={`${fieldInfo.key}-label`} css={fieldLabelStyle}>
        {formatFieldLabel(fieldInfo.label, fieldInfo.required)}
      </Text>

      <div css={slotsContainerStyle}>
        <form.AppField
          name={frontKey}
          validators={{ onChange: () => frontValidator(), onSubmit: () => frontValidator() }}
        >
          {(field) => (
            <CardCaptureSideInput
              type='front'
              fieldKey={frontKey}
              required={required}
              hasError={frontHasError}
              onChange={(mediaId) => field.handleChange(mediaId)}
            />
          )}
        </form.AppField>

        <form.AppField name={backKey} validators={{ onChange: () => backValidator(), onSubmit: () => backValidator() }}>
          {(field) => (
            <CardCaptureSideInput
              type='back'
              fieldKey={backKey}
              required={required}
              hasError={backHasError}
              onChange={(mediaId) => field.handleChange(mediaId)}
            />
          )}
        </form.AppField>
      </div>

      <Text size='small' color='light' css={{ marginTop: theme.spacing(2) }}>
        {t('Maximum size allowed for each file is {{size}} MB', { size: Math.round(MAX_SIZE_MB) })}
      </Text>

      {fieldErrors.length > 0 && (
        <Text size='small' css={fieldErrorStyle}>
          {fieldErrors.join(', ')}
        </Text>
      )}
    </fieldset>
  );
};

const fieldErrorStyle = css({
  color: theme.colors.text.critical,
  marginTop: theme.spacing(1),
});

const fieldsetStyle = css`
  border: none;
  margin: 0;
  padding: 0;
  min-width: 0;
`;

const fieldLabelStyle = css`
  margin-bottom: ${theme.spacing(0.5)};
`;

const slotsContainerStyle = css`
  display: flex;
  gap: ${theme.spacing(3)};
  margin-top: ${theme.spacing(1)};
`;
